"use client";

import React, { useRef, useEffect } from "react";
import ThreeStars from "../../Decorations/ThreeStars/ThreeStars";
import ShootingStar from "../../Decorations/ShootingStar/ShootingStar";
import GlobeYellowWithPrince from "../../Decorations/GlobeYellowWithPrince/GlobeYellowWithPrince";
import PlanetteYellow1 from "../../Decorations/PlanetteYellow1/PlanetteYellow1";
import PlanetteYellow2 from "../../Decorations/PlanetteYellow2/PlanetteYellow2";
import GlobeBlueWithStars from "../../Decorations/GlobeBlueWithStars/GlobeBlueWithStars";
import PlanetteBeige from "../../Decorations/PlanetteBeige/PlanetteBeige";
import GlobeGreen from "../../Decorations/GlobeGreen/GlobeGreen";
import { _HeaderPetitPrinceDecorationBaseContainer } from "./HeaderPetitPrinceDecorationBase.styled";
import { IStyledDivStyles } from "../../../../StyledDiv/StyledDiv";
import StyledDiv from "../../../../StyledDiv/StyledDiv";
import useBreakpoint from "../../../../Grid/hooks/useBreakpoint";
import { lazyGsap } from "../../../../../lib/gsap/lazyGsap";

interface IHeaderPetitPrinceDecorationBaseProps {
  styles?: IStyledDivStyles;
  /** Référence à l'élément trigger pour le scroll (section header) */
  triggerRef?: React.RefObject<HTMLElement>;
  /** Activer l'animation de parallax au scroll */
  enableScrollAnimation?: boolean;
}

// Vitesses de parallax pour chaque décoration (multipliées par la hauteur du viewport)
// 1.0 = suit exactement le scroll, >1 = monte plus vite que le scroll
// { desktop, mobile } - valeurs différentes selon le device
const PARALLAX_SPEEDS = {
  threeStars: { desktop: 0.5, mobile: 0.4 },
  shootingStar: { desktop: 0.7, mobile: 0.55 },
  planetteYellow1: { desktop: 0.6, mobile: 0.5 },
  globeYellowWithPrince: { desktop: 1.2, mobile: 1 },
  planetteYellow2: { desktop: 0.6, mobile: 0.5 },
  globeBlueWithStars: { desktop: 0.8, mobile: 1 },
  planetteBeige: { desktop: 1.4, mobile: 1 },
  globeGreen: { desktop: 1, mobile: 1 },
};

const HeaderPetitPrinceDecorationBase = ({
  styles,
  triggerRef,
  enableScrollAnimation = true,
}: IHeaderPetitPrinceDecorationBaseProps) => {
  const { isMobile } = useBreakpoint();
  const threeStarsRef = useRef<HTMLDivElement>(null);
  const shootingStarRef = useRef<HTMLDivElement>(null);
  const planetteYellow1Ref = useRef<HTMLDivElement>(null);
  const globeYellowWithPrinceRef = useRef<HTMLDivElement>(null);
  const planetteYellow2Ref = useRef<HTMLDivElement>(null);
  const globeBlueWithStarsRef = useRef<HTMLDivElement>(null);
  const plAnetteBeigeRef = useRef<HTMLDivElement>(null);
  const globeGreenRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !enableScrollAnimation) return;

    const trigger = triggerRef?.current || containerRef.current;
    if (!trigger) return;

    let cancelled = false;
    let cleanup: (() => void) | undefined;

    lazyGsap().then(({ gsap, ScrollTrigger }) => {
      if (cancelled) return;

    // Sur mobile : désactiver complètement l'animation
    if (isMobile) {
      // Réinitialise les transforms GSAP pour s'assurer qu'il n'y a pas d'animation résiduelle
      if (containerRef.current) {
        gsap.set(containerRef.current, { clearProps: "y,transform" });
      }
      
      // Nettoyer les transforms des décorations individuelles aussi
      const allDecorationRefs = [
        threeStarsRef,
        shootingStarRef,
        planetteYellow1Ref,
        globeYellowWithPrinceRef,
        planetteYellow2Ref,
        globeBlueWithStarsRef,
        plAnetteBeigeRef,
        globeGreenRef,
      ];
      
      allDecorationRefs.forEach((ref) => {
        if (ref.current) {
          gsap.set(ref.current, { clearProps: "y,transform" });
        }
      });

      return;
    }

    // Sur desktop uniquement : animation des décorations individuelles (code original)
    // Sélectionne la vitesse appropriée selon le device
    const getSpeed = (speeds: { desktop: number; mobile: number }) =>
      speeds.desktop;

    const viewportHeight = window.innerHeight;

    // Définition des décorations avec leurs refs et vitesses
    const decorationsConfig = [
      {
        ref: threeStarsRef,
        speed: getSpeed(PARALLAX_SPEEDS.threeStars),
      },
      {
        ref: shootingStarRef,
        speed: getSpeed(PARALLAX_SPEEDS.shootingStar),
      },
      {
        ref: planetteYellow1Ref,
        speed: getSpeed(PARALLAX_SPEEDS.planetteYellow1),
      },
      {
        ref: globeYellowWithPrinceRef,
        speed: getSpeed(PARALLAX_SPEEDS.globeYellowWithPrince),
      },
      {
        ref: planetteYellow2Ref,
        speed: getSpeed(PARALLAX_SPEEDS.planetteYellow2),
      },
      {
        ref: globeBlueWithStarsRef,
        speed: getSpeed(PARALLAX_SPEEDS.globeBlueWithStars),
      },
      {
        ref: plAnetteBeigeRef,
        speed: getSpeed(PARALLAX_SPEEDS.planetteBeige),
      },
      {
        ref: globeGreenRef,
        speed: getSpeed(PARALLAX_SPEEDS.globeGreen),
      },
    ];

    // Réinitialise les transforms GSAP avant de recréer les animations
    decorationsConfig.forEach(({ ref }) => {
      if (ref.current) {
        gsap.set(ref.current, { clearProps: "y,transform" });
      }
    });

    // Créer les quickSetters pour des performances optimales
    // quickSetter évite la création d'objets à chaque frame
    const setters = decorationsConfig.map(({ ref, speed }) => ({
      setter: ref.current ? gsap.quickSetter(ref.current, "y", "px") : null,
      maxDistance: -viewportHeight * speed,
    }));

    // Variable pour stocker la progression actuelle (pour le lerp)
    let currentProgress = 0;
    let targetProgress = 0;
    let rafId: number | null = null;

    // Facteur de lissage pour desktop
    const smoothFactor = 0.15;

    // Debounce/throttle pour limiter les mises à jour lors d'un scroll rapide
    let lastUpdateTime = 0;
    const throttleDelay = 16; // ~60fps (environ 16ms entre chaque frame)
    let pendingProgress: number | null = null;
    let throttleTimeoutId: number | null = null;

    // Fonction d'animation avec lerp pour un mouvement ultra-fluide
    const animate = () => {
      // Interpolation linéaire pour lisser le mouvement
      currentProgress += (targetProgress - currentProgress) * smoothFactor;

      // Appliquer les transformations
      setters.forEach(({ setter, maxDistance }) => {
        if (setter) {
          setter(currentProgress * maxDistance);
        }
      });

      // Continuer l'animation si on n'a pas atteint la cible
      if (Math.abs(targetProgress - currentProgress) > 0.0001) {
        rafId = requestAnimationFrame(animate);
      } else {
        rafId = null;
      }
    };

    // Fonction pour mettre à jour la progression avec throttle
    const updateProgress = (progress: number) => {
      const now = performance.now();
      const timeSinceLastUpdate = now - lastUpdateTime;

      if (timeSinceLastUpdate >= throttleDelay) {
        // Mise à jour immédiate si assez de temps s'est écoulé
        targetProgress = progress;
        lastUpdateTime = now;
        pendingProgress = null;

        // Démarrer l'animation si elle n'est pas déjà en cours
        if (rafId === null) {
          rafId = requestAnimationFrame(animate);
        }
      } else {
        // Stocker la progression pour une mise à jour différée
        pendingProgress = progress;

        // Annuler le timeout précédent s'il existe
        if (throttleTimeoutId !== null) {
          cancelAnimationFrame(throttleTimeoutId);
        }

        // Programmer une mise à jour après le délai
        throttleTimeoutId = requestAnimationFrame(() => {
          if (pendingProgress !== null) {
            targetProgress = pendingProgress;
            lastUpdateTime = performance.now();
            pendingProgress = null;
            throttleTimeoutId = null;

            // Démarrer l'animation si elle n'est pas déjà en cours
            if (rafId === null) {
              rafId = requestAnimationFrame(animate);
            }
          }
        });
      }
    };

    // Un seul ScrollTrigger pour toutes les décorations
    const scrollTrigger = ScrollTrigger.create({
      trigger: trigger,
      start: "top top",
      end: "bottom top",
      onUpdate: (self) => {
        updateProgress(self.progress);
      },
      fastScrollEnd: true,
      preventOverlaps: true,
    });

    // Force un refresh de ScrollTrigger après la création
    ScrollTrigger.refresh();

    cleanup = () => {
      // Annuler l'animation en cours
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      // Annuler le throttle timeout
      if (throttleTimeoutId !== null) {
        cancelAnimationFrame(throttleTimeoutId);
      }

      scrollTrigger.kill();

      // Réinitialise les transforms lors du cleanup
      decorationsConfig.forEach(({ ref }) => {
        if (ref.current) {
          gsap.set(ref.current, { clearProps: "y,transform" });
        }
      });
    };
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [enableScrollAnimation, triggerRef, isMobile]);

  return (
    <StyledDiv ref={containerRef} styles={styles}>
      <_HeaderPetitPrinceDecorationBaseContainer>
        {/* Top decorations */}
        <StyledDiv
          ref={threeStarsRef}
          styles={{
            position: "absolute",
            top: 0,
            left: "38%",
            zIndex: 1,
            willChange: "transform",
            backfaceVisibility: "hidden",
            mobile: {
              display: "none",
            },
          }}
          className="three-stars"
        >
          <StyledDiv styles={{ transform: "translateX(-50%)" }}>
            <ThreeStars />
          </StyledDiv>
        </StyledDiv>
        <StyledDiv
          ref={shootingStarRef}
          styles={{
            position: "absolute",
            top: "7%",
            left: "54%",
            zIndex: 1,
            willChange: "transform",
            backfaceVisibility: "hidden",
            mobile: {
              left: "unset",
              right: "10%",
              top: "-64px",
            },
          }}
        >
          <StyledDiv
            styles={{
              mobile: {
                scale: "0.34",
                transformOrigin: "100% center",
              },
            }}
          >
            <ShootingStar />
          </StyledDiv>
        </StyledDiv>
        <StyledDiv
          ref={planetteYellow1Ref}
          styles={{
            position: "absolute",
            top: 0,
            zIndex: 1,
            display: "none",
            willChange: "transform",
            backfaceVisibility: "hidden",
            mobile: {
              display: "block",
              left: "2%",
              top: "12%",
            },
          }}
        >
          <PlanetteYellow1
            styles={{
              mobile: {
                scale: "0.5",
              },
            }}
          />
        </StyledDiv>

        {/* Left decorations */}
        <StyledDiv
          ref={globeYellowWithPrinceRef}
          styles={{
            position: "absolute",
            left: "-24%",
            top: "50%",
            zIndex: 1,
            willChange: "transform",
            backfaceVisibility: "hidden",
            mobile: {
              top: "35%",
              left: "0",
            },
          }}
          className="globe-yellow-with-prince"
        >
          <StyledDiv
            styles={{
              mobile: {
                scale: "0.5",
                transformOrigin: "left center",
                transform: "translate(-50%, 0%)",
              },
            }}
          >
            <GlobeYellowWithPrince styles={{}} />
          </StyledDiv>
        </StyledDiv>

        {/* Right decorations */}
        <StyledDiv
          ref={planetteYellow2Ref}
          styles={{
            position: "absolute",
            top: "40%",
            right: "17%",
            zIndex: 1,
            willChange: "transform",
            backfaceVisibility: "hidden",
            mobile: {
              display: "none",
            },
          }}
        >
          <PlanetteYellow2 />
        </StyledDiv>
        <StyledDiv
          ref={globeBlueWithStarsRef}
          styles={{
            position: "absolute",
            top: "67%",
            right: "23%",
            zIndex: 1,
            willChange: "transform",
            backfaceVisibility: "hidden",
            mobile: {
              right: "3%",
              top: "64%",
            },
          }}
          className="globe-blue-with-stars"
        >
          <GlobeBlueWithStars
            styles={{
              mobile: {
                scale: "0.5",
              },
            }}
          />
        </StyledDiv>
        <StyledDiv
          ref={plAnetteBeigeRef}
          styles={{
            position: "absolute",
            bottom: 0,
            right: 0,
            zIndex: 1,
            willChange: "transform",
            backfaceVisibility: "hidden",
            mobile: {
              transformOrigin: "right center",
              bottom: "10%",
            },
          }}
          className="planette-beige"
        >
          <StyledDiv styles={{ transform: "translate(50%, 80%)" }}>
            <PlanetteBeige
              styles={{
                mobile: {
                  scale: "0.45",
                },
              }}
            />
          </StyledDiv>
        </StyledDiv>

        {/* Bottom center decoration */}
        <StyledDiv
          ref={globeGreenRef}
          styles={{
            position: "absolute",
            top: "80%",
            left: "50%",
            zIndex: 1,
            willChange: "transform",
            backfaceVisibility: "hidden",
            mobile: {
              transformOrigin: "center center",
              top: "72%",
              left: "60%",
            },
          }}
          className="globe-green"
        >
          <StyledDiv
            styles={{
              transform: "translate(-50%, 60%)",
              mobile: {
                transformOrigin: "center center",
              },
            }}
          >
            <GlobeGreen
              styles={{
                mobile: {
                  scale: "0.5",
                },
              }}
            />
          </StyledDiv>
        </StyledDiv>
      </_HeaderPetitPrinceDecorationBaseContainer>
    </StyledDiv>
  );
};

export default HeaderPetitPrinceDecorationBase;
